(() => {
  const TARGET_CODE = 'GOODLIFE';
  const CART_REDIRECT_PATH = '/cart';
  const CHECKOUT_REDIRECT_PATH = '/checkout';
  const COUPON_CONTAINER_SELECTOR = '.docapp-coupon-input';
  const COUPON_INPUT_SELECTOR = '.docapp-coupon-input--input';
  const COUPON_BUTTON_SELECTOR = '.docapp-coupon-input--button';
  const MESSAGE_CONTAINER_SELECTOR = '.docapp-coupon-input--message-container';
  const MESSAGE_CONTENT_SELECTOR = '.docapp-coupon-input--message-content';
  const ALERT_CLASSES = {
    success: 'docapp-alert-success',
    danger: 'docapp-alert-danger',
    neutral: 'docapp-alert-neutral'
  };

  const JUST_APPLIED_STORAGE_KEY = 'tci-goodlife-just-applied';
  let applyInFlight = false;

  function normalizeCode(value) {
    return (value || '').trim().toUpperCase();
  }

  function isGoodlifeCode(value) {
    return normalizeCode(value) === TARGET_CODE;
  }

  function buildDiscountUrl(code, redirectPath) {
    return `/discount/${encodeURIComponent(normalizeCode(code))}?redirect=${encodeURIComponent(redirectPath)}`;
  }

  function getCouponContainer(element) {
    return element instanceof Element ? element.closest(COUPON_CONTAINER_SELECTOR) : null;
  }

  function getCouponInput(container) {
    return container ? container.querySelector(COUPON_INPUT_SELECTOR) : null;
  }

  function getCouponCode(container) {
    const input = getCouponInput(container);
    return input ? normalizeCode(input.value) : '';
  }

  function showMessage(container, tone, message) {
    const messageContainer = container ? container.querySelector(MESSAGE_CONTAINER_SELECTOR) : null;
    const messageContent = container ? container.querySelector(MESSAGE_CONTENT_SELECTOR) : null;

    if (!messageContainer || !messageContent) {
      return;
    }

    Object.entries(ALERT_CLASSES).forEach(([key, className]) => {
      messageContainer.classList.toggle(className, key === tone);
    });

    messageContent.textContent = message;
    messageContainer.classList.remove('docapp-hide');
  }

  function showSuccessMessage() {
    const container = document.querySelector(COUPON_CONTAINER_SELECTOR);

    if (!container) {
      return;
    }

    const input = getCouponInput(container);

    if (input) {
      input.value = TARGET_CODE;
    }

    showMessage(container, 'success', `Coupon code '${TARGET_CODE}' applied.`);
  }

  function applyPendingSuccessMessage() {
    if (window.sessionStorage.getItem(JUST_APPLIED_STORAGE_KEY) !== '1') {
      return;
    }

    window.sessionStorage.removeItem(JUST_APPLIED_STORAGE_KEY);
    window.setTimeout(showSuccessMessage, 200);
  }

  function refreshCartUi() {
    const openCartDrawer = document.querySelector('cart-drawer[open]');

    if (window.location.pathname === CART_REDIRECT_PATH) {
      window.sessionStorage.setItem(JUST_APPLIED_STORAGE_KEY, '1');
      window.location.replace(CART_REDIRECT_PATH);
      return;
    }

    if (openCartDrawer) {
      const handleAfterMerge = () => {
        document.removeEventListener('on:cart:after-merge', handleAfterMerge, true);
        showSuccessMessage();
      };

      document.addEventListener('on:cart:after-merge', handleAfterMerge, true);
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      return;
    }

    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    window.setTimeout(showSuccessMessage, 300);
  }

  async function applyGoodlifeCode(container, redirectPath) {
    if (applyInFlight) {
      return;
    }

    applyInFlight = true;

    if (container) {
      const input = getCouponInput(container);

      if (input) {
        input.value = TARGET_CODE;
      }

      showMessage(container, 'neutral', `Applying coupon code '${TARGET_CODE}'...`);
    }

    try {
      const response = await window.fetch(buildDiscountUrl(TARGET_CODE, CART_REDIRECT_PATH), {
        credentials: 'same-origin',
        redirect: 'follow'
      });

      if (!response.ok) {
        throw new Error(`GOODLIFE apply failed with status ${response.status}`);
      }

      if (redirectPath) {
        window.location.assign(redirectPath);
        return;
      }

      refreshCartUi();
    } catch (error) {
      showMessage(container || document.querySelector(COUPON_CONTAINER_SELECTOR), 'danger', `Coupon code '${TARGET_CODE}' could not be applied.`);
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      applyInFlight = false;
    }
  }

  function interceptManualApply(event, redirectPath) {
    const container = getCouponContainer(event.target);

    if (!container || !isGoodlifeCode(getCouponCode(container))) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyGoodlifeCode(container, redirectPath);
    return true;
  }

  function patchDiscountOnCartPro() {
    const app = window.discountOnCartProApp;

    if (!app || app.__tciGoodlifeBridgeInstalled) {
      return Boolean(app && app.__tciGoodlifeBridgeInstalled);
    }

    const originalApplyCode = typeof app.applyCode === 'function' ? app.applyCode.bind(app) : null;
    const originalApplyCodeAndCheckout = typeof app.applyCodeAndCheckout === 'function' ? app.applyCodeAndCheckout.bind(app) : null;

    app.applyCode = (code) => {
      if (isGoodlifeCode(code)) {
        if (!applyInFlight) {
          applyGoodlifeCode(null, null);
        }

        return;
      }

      if (originalApplyCode) {
        return originalApplyCode(code);
      }
    };

    app.applyCodeAndCheckout = (code) => {
      if (isGoodlifeCode(code)) {
        if (!applyInFlight) {
          applyGoodlifeCode(null, CHECKOUT_REDIRECT_PATH);
        }

        return;
      }

      if (originalApplyCodeAndCheckout) {
        return originalApplyCodeAndCheckout(code);
      }
    };

    app.__tciGoodlifeBridgeInstalled = true;
    return true;
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest(COUPON_BUTTON_SELECTOR)) {
      return;
    }

    interceptManualApply(event, null);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof Element) || !event.target.closest(COUPON_INPUT_SELECTOR)) {
      return;
    }

    interceptManualApply(event, null);
  }, true);

  document.addEventListener('submit', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const container = getCouponContainer(event.target);

    if (!container || !isGoodlifeCode(getCouponCode(container))) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyGoodlifeCode(container, null);
  }, true);

  if (!patchDiscountOnCartPro()) {
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;

      if (patchDiscountOnCartPro() || attempts >= 60) {
        window.clearInterval(intervalId);
      }
    }, 500);
  }

  applyPendingSuccessMessage();
})();
